import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useUserPaymentTransactions() {
    const { userId } = useParams(); // ✅ take userId from params
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const { send, isConnected, connect, registerHandler } = useWebSocketContext();
    const mountedRef = useRef(true);
    const retryTimeoutRef = useRef(null);

    const fetchTransactions = useCallback(async (attempt = 0) => {
        if (!mountedRef.current || !userId) return;

        setIsLoading(true);
        setError("");
        setMessage("");

        try {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }

            if (!isConnected) {
                return;
            }

            const response = await send("cms_get_all_user_payment_transactions", {
                user_id: userId, // ✅ only send userId
            });

            console.log(`Payment Transactions for user ${userId}:`, response);

            if (!response || response.type !== "cms_get_all_user_payment_transactions_response") {
                throw new Error("Invalid response from server");
            }

            if (!Array.isArray(response.data)) {
                throw new Error("No data available. Try refreshing the page");
            }

            setTransactions(response.data);
            setMessage(response.message || "Transactions fetched successfully");
        } catch (err) {
            setError(err.message);

            if (mountedRef.current) {
                const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
                retryTimeoutRef.current = setTimeout(() => {
                    fetchTransactions(attempt + 1);
                }, delay);
            }
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [isConnected, connect, send, userId]);

    useEffect(() => {
        mountedRef.current = true;
        fetchTransactions();

        const unregister = registerHandler(`user_payment_transactions_update_${userId}`, (msg) => {
            if (Array.isArray(msg.data) && mountedRef.current) {
                setTransactions(msg.data);
            }
        });

        return () => {
            mountedRef.current = false;
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            unregister();
        };
    }, [fetchTransactions, registerHandler, userId]);

    return { transactions, isLoading, error, message, refetch: () => fetchTransactions(0) };
}
